import { inject, Injectable } from '@angular/core';
import { CartKeys } from '@config/keys/cart.keys';
import { EvaluateBlockVisibilityService } from '@core/services/content-builder/evaluate-block-visibility.service';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { CartStoreSelector } from '@common/selectors/cart.selector';
import {
  CART_FUNNEL_STORE_GENERATED_ACTIONS,
  CART_FUNNEL_STORE_LOCAL_ACTIONS,
  CartFunnelFormBlock,
} from '@store/cart-funnel';
import {
  SELECT_CART_FUNNEL_ACTIVE_STEP_ID,
  SELECT_CART_FUNNEL_STEPS_ALL,
} from '@store/cart-funnel/cart-funnel.selection';
import { SELECT_CONTENT_BLOCKS_ALL } from '@store/content';
import {
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  switchMap,
  takeUntil,
  withLatestFrom,
} from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class CartFunnelEffects {
  syncActiveCartIdEffect = createEffect((cartSelector = inject(CartStoreSelector)) => {
    return cartSelector.selectActive$.pipe(
      map(cart => cart?.[CartKeys.CartId] ?? cart?.[CartKeys.Handle] ?? null),
      distinctUntilChanged(),
      map(cartId => CART_FUNNEL_STORE_LOCAL_ACTIONS.setActiveCartId({ cartId })),
    );
  });

  determineCartFunnelDisplayableStepsEffect = createEffect(
    (
      actions$ = inject(Actions),
      store = inject(Store),
      evaluateBlockVisibilityService = inject(EvaluateBlockVisibilityService),
    ) => {
      return actions$.pipe(
        ofType(CART_FUNNEL_STORE_LOCAL_ACTIONS.addStep),
        switchMap(() => store.select(SELECT_CART_FUNNEL_STEPS_ALL)),

        switchMap(allStepsBlockIds =>
          store.select(SELECT_CONTENT_BLOCKS_ALL).pipe(
            switchMap(allBlocks => {
              // Build an array of observables for each block visibility
              const funnelFormBlocksWithVisibility = allStepsBlockIds
                .map(
                  ({ id: stepBlockId }) =>
                    allBlocks.find(block => block.id === stepBlockId) as CartFunnelFormBlock,
                )
                .map(stepBlock => {
                  const items = stepBlock?.data?.items?.map(item =>
                    allBlocks.find(block => block.id === item.block),
                  );
                  return {
                    ...stepBlock,
                    data: {
                      ...stepBlock.data,
                      items,
                    },
                  };
                })
                .filter(stepBlock => stepBlock !== undefined)
                .map(stepBlock => {
                  const stepExpression =
                    stepBlock?.data?.common?.conditionalVisibility?.expression ?? 'true';

                  // Evaluate each item's visibility individually
                  const itemVisibilityObservables =
                    stepBlock?.data?.items?.map(item => {
                      const itemExpression =
                        item?.data?.common?.conditionalVisibility?.expression ?? 'true';
                      return evaluateBlockVisibilityService
                        .evaluate$(itemExpression, {
                          additionalContext: stepBlock?.data?.common?.variables,
                        })
                        .pipe(
                          distinctUntilChanged(),
                          map(expressionResult => !!expressionResult),
                        );
                    }) || [];

                  // Combine step visibility with items visibility
                  return combineLatest([
                    evaluateBlockVisibilityService.evaluate$(stepExpression, {
                      additionalContext: stepBlock?.data?.common?.variables,
                    }),
                    ...itemVisibilityObservables,
                  ]).pipe(
                    distinctUntilChanged(),
                    map(([stepVisible, ...itemsVisible]) => {
                      // Step is visible only if the step itself is visible AND at least one item is visible
                      const isOnSeatStep = stepBlock.id === 'cart-funnel-seat-count-item-block';
                      const hasVisibleItems = itemsVisible.some(itemVisible => itemVisible); // todo handle seatCount no items
                      const isStepVisible = !!stepVisible && (hasVisibleItems || isOnSeatStep);
                      return {
                        ...stepBlock,
                        isStepVisible,
                      };
                    }),
                  );
                });

              // Combine all block observables into one observable emitting an array
              return combineLatest(funnelFormBlocksWithVisibility);
            }),
            takeUntil(actions$.pipe(ofType(CART_FUNNEL_STORE_LOCAL_ACTIONS.leaveCartFunnel))),
          ),
        ),
        map(allStepsBlocks =>
          CART_FUNNEL_STORE_LOCAL_ACTIONS.setDisplayableStepsIds({
            displayableStepsIds: allStepsBlocks
              .filter(block => block.isStepVisible)
              .map(block => block.id) as string[],
          }),
        ),
      );
    },
  );

  handleInitialCartFunnelActiveStepEffect = createEffect(
    (actions$ = inject(Actions), store = inject(Store)) => {
      return actions$.pipe(
        ofType(CART_FUNNEL_STORE_LOCAL_ACTIONS.setDisplayableStepsIds),
        withLatestFrom(
          store.select(SELECT_CART_FUNNEL_ACTIVE_STEP_ID),
          store.select(SELECT_CART_FUNNEL_STEPS_ALL),
        ),
        filter(([action, activeStepId, allSteps]) => {
          if (action.displayableStepsIds.length === 0) {
            return false;
          }

          if (!activeStepId) {
            return true;
          }

          return (
            !allSteps.some(step => step.id === activeStepId) ||
            !action.displayableStepsIds.includes(activeStepId)
          );
        }),
        map(([action]) => {
          return CART_FUNNEL_STORE_GENERATED_ACTIONS.setActive({
            active: action.displayableStepsIds[0],
          });
        }),
      );
    },
  );
}
